
import {Region} from 'utils/region';

export type RegionSource = 'ORDER' | 'IP' | 'COOKIE' | 'GPS' | 'URL' | 'MANUAL';

export const SOURCE_PRIORITY: Record<RegionSource, number> = {
	// A metro implied by an ordering region (?region=texas picks Austin);
	// where the visitor actually is replaces it
	ORDER: 0,
	IP: 1,
	GPS: 2,
	COOKIE: 3,
	URL: 4,
	MANUAL: 5,
};

export type RegionState = {
	region?: Region
	coords?: {lat: number; lng: number}
	label?: string
	source?: RegionSource

	// The user's actual browser/IP geolocation coords, set once on
	// page load. Unlike `coords` (which tracks the active search
	// target and resets on clear), geoCoords survives CLEAR_REGION
	// so distance sorting and the scheduler can still reference the
	// user's real position. Also signals that geolocation finished,
	// even if the user is outside all service regions.
	geoCoords?: {lat: number; lng: number}

	// The ordering region (e.g. new_york) whose products the visitor
	// sees, decided once per page view (orderSettled): from ?region=,
	// a booking or checkout, its cookie, or else the metro's best
	// guess (see pickOrderRegion). Undefined sells national products.
	orderRegion?: string
	orderSettled?: boolean

	// Set once the cookie and URL have been read; geolocation may
	// still be pending.
	ready?: boolean
}

export type RegionAction =
	| {type: 'SET_REGION'; region: Region; coords?: {lat: number; lng: number}; source: RegionSource}
	| {type: 'SET_COORDS'; coords: {lat: number; lng: number}; label?: string; skipIf?: 'mismatched'; source?: RegionSource}
	| {type: 'GEO_COMPLETE'; coords: {lat: number; lng: number}}
	| {type: 'SET_ORDER_REGION'; orderRegion: string}
	| {type: 'SETTLE_ORDER_REGION'}
	| {type: 'READY'}
	| {type: 'CLEAR_REGION'}

export type RegionContextValue = {
	state: RegionState

	// The ordering region whose products the visitor sees, e.g.
	// new_york in nyc_metro; undefined sells national products. Prices
	// wait for orderSettled so the visitor sees one price.
	orderRegion?: string
	orderSettled: boolean

	// Records an ordering region the visitor acted in (a booking or
	// checkout at a location); it replaces any earlier one
	chooseOrderRegion: (orderRegion: string) => void

	setRegion: (region: Region) => void
	setCoords: (coords: {lat: number; lng: number}, label?: string) => void
	clearRegion: () => void
	skipGeolocation: () => void
}
